import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { forkJoin, interval, Observable, of, Subscription, catchError, takeUntil, Subject, switchMap, filter } from 'rxjs';
import { PageEvent } from '@angular/material/paginator';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';

import { PaginationResult, PaginatorParams } from '@api/models/paginator';
import { BrokenScreensResponse, OfflineScreens, BookingsMetricsModel, ScreenAliveStatusEnum, UnusedScreen } from './start.models';
import { BookingRouterState } from '@shared/models/enums/bookingRouterState.enum';
import { ManualPage } from '@shared/models/enums/manualPage.enum';
import { TableData } from '@app/models/table-data';
import { TranslateConfigService } from '@core/translate-config.service';
import { AuthService } from '@core/auth/auth.service';
import { UserRoleEnum } from '@app/admin/user/model/user.model';
import { CustomerApi, ScreenApi } from '@api/index';
import { BookingApi } from '@api/services/bookings-api';
import { DeviceTypeTranslations } from '@shared/models/enums/deviceType.enum';
import { DeviceService } from '@core/services/device.service';
import { TrackingTranslations } from '@shared/models/enums/deviceTracking.enum';
import { CurrentUser } from '@core/auth/current-user';
import { SidePanelService } from '@shared/components/side-panel/side-panel.service';
import { Permissions } from '@shared/models/enums/permissions.enum';

@Component({
  selector: 'flow-start',
  templateUrl: './start.component.html',
  styleUrls: ['./start.component.scss'],
})
export class StartComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();
  isReady: boolean = false;
  subscription: Subscription;
  customerId: number;
  intervalTime = 60000;
  firstName: string = '';
  currentTime: DateTime = DateTime.local();
  isAdmin: boolean;
  displayedColumnsOffline: string[] = ['status', 'name', 'type', 'customer', 'channel', 'placement', 'tracking', 'lastOnline'];
  displayedColumns: string[] = ['id', 'week', 'name', 'channel', 'placement', 'screenLayout', 'mediaName', 'actions'];
  dataSource: MatTableDataSource<UnusedScreen>;
  dataSourceOffline: MatTableDataSource<OfflineScreens>;
  numberOfScreens: number | null = null;
  numbersOfWeekForFiltering: number;
  TrackingEnum = TrackingTranslations;
  DeviceTypeTranslations = DeviceTypeTranslations;
  ScreenAliveStatusEnum: ScreenAliveStatusEnum;
  sortObjectOffline: any = { active: 'name', direction: 'asc' };
  sortObject: any = { active: 'week', direction: 'asc' };
  data: TableData = { actionButtons: [], dataModels: [], dataSource: new MatTableDataSource<UnusedScreen>([]), displayedColumns: [] };
  dataOffline: TableData = {
    actionButtons: [],
    dataModels: [],
    dataSource: new MatTableDataSource<OfflineScreens>([]),
    displayedColumns: [],
  };
  screensWithIssues: BrokenScreensResponse = {
    screensAndSensorsCount: 0,
    onlineScreensAndSensorsCount: 0,
    offlineScreensAndSensorsCount: 0,
    screensWithIssues: [],
  };
  unusedScreens: UnusedScreen[];
  bookingsMetrics: BookingsMetricsModel = {
    bookingsCountForThisPeriod: 0,
    segmentCountForThisPeriod: 0,
    playlistCountForThisPeriod: 0,
    bookingsCountForPreviousPeriod: 0,
    segmentCountForPreviousPeriod: 0,
    playlistCountForPreviousPeriod: 0,
  };
  totalCount: number = 0;
  pageSizeOptions = [5, 10, 25, 100];
  params: PaginatorParams = {
    pageNumber: 0,
    pageSize: 10,
    sortBy: 'week',
    sortDirection: 'asc',
  };
  brokenScreensParams = {
    sortBy: 'name',
    sortDirection: 'asc',
  };
  @ViewChild('sortBroken') sortBroken: MatSort;
  @ViewChild('sortUnused') sortUnused: MatSort;
  brokenScreens: BrokenScreensResponse | null = null;
  Permissions = Permissions;

  constructor(
    public translateConfigService: TranslateConfigService,
    private authService: AuthService,
    private customerApi: CustomerApi,
    private screenApi: ScreenApi,
    private bookingsApi: BookingApi,
    private router: Router,
    private datePipe: DatePipe,
    private deviceService: DeviceService,
    protected translateService: TranslateService,
    private sidePanelService: SidePanelService
  ) { }

  ngOnInit(): void {
    const user = this.authService.userData;
    this.firstName = user?.fullName?.split(' ')[0];
    this.currentTime = DateTime.local();

    this.getData(user);

    this.translateService.onLangChange
      .pipe(
        filter(() => this.brokenScreens?.screensWithIssues.length > 0),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.computeScreenStatuses(this.brokenScreens.screensWithIssues);
      });

    this.subscription = interval(this.intervalTime)
      .pipe(
        switchMap(() => {
          const params = this.customerId ? { customerId: this.customerId, ...this.brokenScreensParams } : { ...this.brokenScreensParams };
          return this.screenApi.getBrokenScreens(params);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((screens: BrokenScreensResponse) => {
        this.getBrokenScreens(screens);
      });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  getData(user: CurrentUser) {
    this.customerId = user?.currentCustomerId || user?.customerId;
    this.isAdmin = user.roleId === UserRoleEnum.Admin && !user.currentCustomerId;
    if (this.isAdmin) {
      const index = this.displayedColumns.indexOf('channel');
      this.displayedColumns.splice(index, 0, 'customer');
    }
    this.isReady = false;
    const currentLocalDate = DateTime.local().toISODate();

    forkJoin([
      // this.screenApi.getUnusedScreens(this.getUnusedScreensParams()).pipe(catchError(error => this.handleApiError(error))),
      this.screenApi
        .getBrokenScreens(this.customerId ? { customerId: this.customerId, ...this.brokenScreensParams } : { ...this.brokenScreensParams })
        .pipe(catchError(error => this.handleApiError(error))),
      this.bookingsApi.getBookingsMetrics(currentLocalDate, this.customerId).pipe(catchError(error => this.handleApiError(error))),
      !this.isAdmin ? this.customerApi.getCustomerById(this.customerId) : of(null),
    ])
      .pipe(takeUntil(this.destroy$))
      //unusedScreens
      .subscribe(([brokenScreens, bookingsMetrics, customer]) => {
        this.numbersOfWeekForFiltering = customer?.numbersOfWeekForFiltering || 4;
        // if (unusedScreens) {
        //   this.getUnusedScreens(unusedScreens);
        // }
        if (brokenScreens) {
          this.brokenScreens = brokenScreens;
          this.getBrokenScreens(brokenScreens);
        }
        if (bookingsMetrics) {
          this.bookingsMetrics = bookingsMetrics;
        }
      });
  }
  
  private getBrokenScreens(brokenScreens?: BrokenScreensResponse) {
    // Set data source for offline table
    this.screensWithIssues = brokenScreens;
    this.dataSourceOffline = new MatTableDataSource(brokenScreens.screensWithIssues);
    this.dataSourceOffline.sort = this.sortBroken;
    this.dataOffline.dataSource = this.dataSourceOffline;
    this.dataOffline.displayedColumns = this.displayedColumnsOffline;
    this.computeScreenStatuses(brokenScreens.screensWithIssues);
    this.formatDate(brokenScreens.screensWithIssues.map(screen => screen.screenServicesStateData.lastAliveDate));
  }

  // private getUnusedScreens(unusedScreens?: PaginationResult<UnusedScreen>) {
  //   this.numberOfScreens = unusedScreens?.items.length;

  //   // Set data source for the table
  //   this.unusedScreens = unusedScreens.items;
  //   this.dataSource = new MatTableDataSource(this.unusedScreens);
  //   this.params = { ...this.params, pageNumber: unusedScreens?.currentPage };
  //   this.totalCount = unusedScreens?.totalCount;
  //   this.data.dataSource = this.dataSource;
  //   this.data.displayedColumns = this.displayedColumns;
  //   this.isReady = true;
  // }

  // private getUnusedScreensParams() {
  //   let response: any = this.params;

  //   response = {
  //     ...this.params,
  //     currentLocalDate: DateTime.local().toISODate(),
  //     ...(this.customerId && {customerId: this.customerId})
  //   }

  //   return response;
  // }

  private handleApiError(error: any): Observable<null> {
    return of(null);
  }

  onEngagementBookClick(route: string, screen: UnusedScreen) {
    // send the date here
    const data = {
      customer: screen?.customer,
      channels: [screen?.channel],
      placements: [screen?.placement],
      startDate: screen?.startDate,
      weekNumber: screen?.weekNumber,
    };

    this.router.navigate([route], {
      state: {
        data,
        bookState: BookingRouterState.CreateHome,
      },
    });
  }

  getTimeOfDay(): string {
    const hour = this.currentTime.hour;
    if (hour >= 0 && hour < 12) {
      return this.translateConfigService.instant('home.morning');
    } else if (hour >= 12 && hour < 18) {
      return this.translateConfigService.instant('home.afternoon');
    } else {
      return this.translateConfigService.instant('home.evening');
    }
  }

  getTimeIconName(): string {
    const hour = this.currentTime.hour;
    if (hour >= 0 && hour < 12) {
      return 'morning';
    } else if (hour >= 12 && hour < 18) {
      return 'afternoon';
    } else {
      return 'evening';
    }
  }

  checkNumber(numOfScreens: number) {
    return isNaN(numOfScreens);
  }

  redirectToEditPage(customerId: number): void {
    this.router.navigate(['/devices', customerId]);
  }

  formatDate(dates: string[]) {
    dates.forEach(date => {
      return date ? this.datePipe.transform(date, 'yyyy-MM-dd HH:mm:ss') : '';
    });
  }

  sortData(sort: any, dataSource: any): void {
    sort = { active: sort.active, direction: sort.direction };
    const data: Array<any> = dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      dataSource.data = data;
      return;
    }
    dataSource = new MatTableDataSource<any>(dataSource.data);
  }
  handleSortBrokenEvent(sort: Sort) {
    this.isReady = false;
    this.brokenScreensParams = { sortBy: sort?.active, sortDirection: sort.direction };
    this.screenApi
      .getBrokenScreens(this.customerId ? { customerId: this.customerId, ...this.brokenScreensParams } : { ...this.brokenScreensParams })
      .pipe(takeUntil(this.destroy$))
      .subscribe(brokenScreens => {
        this.getBrokenScreens(brokenScreens);
      });
  }

  // handleSortEvent(sort: Sort) {
  //   this.isReady = false;

  //   this.params = {
  //     ...this.params,
  //     pageNumber: 0,
  //     sortBy: sort?.active,
  //     sortDirection: sort.direction || (this.params.sortDirection == 'asc' ? 'desc' : 'asc') || 'asc',
  //   };
  //   this.screenApi
  //     .getUnusedScreens(this.getUnusedScreensParams())
  //     .pipe(takeUntil(this.destroy$))
  //     .subscribe(unusedScreens => {
  //       this.getUnusedScreens(unusedScreens);
  //     });
  // }

  // handlePageEvent(e: PageEvent) {
  //   this.isReady = false;
  //   this.params = { ...this.params, pageNumber: e.pageIndex, pageSize: e.pageSize };
  //   this.screenApi
  //     .getUnusedScreens(this.getUnusedScreensParams())
  //     .pipe(takeUntil(this.destroy$))
  //     .subscribe(unusedScreens => {
  //       this.getUnusedScreens(unusedScreens);
  //     });
  // }

  computeScreenStatuses(screens: OfflineScreens[]): void {
    this.dataSourceOffline.data = screens.map(screen => {
      const status = this.deviceService.serviceStatus(screen.screenServicesStateData, screen.type);
      return { ...screen, status } as OfflineScreens;
    });
  }

  toggleSidePanel() {
    const config = {
      title: 'contextualModal.home.title',
      manualKey: ManualPage.Home,
      children: [
        {
          title: 'contextualModal.home.whatIsThis.title',
          description: 'contextualModal.home.whatIsThis.description',
        },
        {
          title: 'contextualModal.home.whyIsItImportant.title',
          description: 'contextualModal.home.whyIsItImportant.description',
        },
        {
          title: 'contextualModal.home.howDoIUseThisFeature.title',
          description: 'contextualModal.home.howDoIUseThisFeature.description',
        },
      ],
    };

    this.sidePanelService.openSidePanel(config);
  }
}
